import cn from 'clsx'
import { Box } from '@/components/Box/Box'
import { Card, type CardProps } from '@/components/Card/Card'
import { type BaseImageProps, Image } from '@/components/image/image'
import { Text } from '@/components/Text/Text'

import css from './collection-card.module.css'

type CollectionCardVariant = 'integrations' | 'templates'

type CollectionCardLayout = 'auto' | 'stacked' | 'sideBySide'

export type CollectionCardProps = {
    title: string
    description?: string
    variant: CollectionCardVariant
    layout?: CollectionCardLayout
    icons: BaseImageProps[]
    href: string
    target?: '_blank' | '_self'
}

export function CollectionCard({
    title,
    description,
    href,
    target = '_blank',
    icons = [],
    variant,
    layout = 'auto',
    className,
    ...rest
}: CollectionCardProps & Omit<CardProps, 'children'>) {
    return (
        <Box className={cn(css.container, css[layout])}>
            <Card className={cn(css.collectionCard, className)} {...rest}>
                <IconStack icons={icons} variant={variant} />
                <Box className={css.content}>
                    <a className={css.link} href={href} target={target}>
                        <Text size="minor-lg" weight="semibold" title={title}>
                            {title}
                        </Text>
                    </a>
                    {description ? (
                        <Text size="body-sm" weight="medium" className={cn(css.description)}>
                            {description}
                        </Text>
                    ) : null}
                </Box>
            </Card>
        </Box>
    )
}

function IconStack({
    icons,
    variant,
}: {
    icons: BaseImageProps[]
    variant: CollectionCardVariant
}) {
    const imgSize = variant === 'templates' ? 94 : 36

    return (
        <Box className={css.iconStack}>
            {icons.map((icon) => (
                <Box className={`${css.icon} ${css[variant]}`} key={icon.src}>
                    <Image width={imgSize} height={imgSize} alt="" {...icon} />
                </Box>
            ))}
        </Box>
    )
}
